// event-registration-form-app/App.jsx

// saving draft doesn't return payment amount.

import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import _, { set } from "lodash";
import AttendeeFields from "./components/AttendeeFields";
import FormField from "./components/FormField";
import AlertBar from "./components/AlertBar";
import LoadingIndicator from "./components/LoadingIndicator";
import StripeProvider from "./components/StripeProvider";

const blankFormValues = {
  submit_type: "",
  // attendees: [{}], // Just let the add attendee button sort it out
};

const App = () => {
  // Set up the state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [initialValues, setInitialValues] = useState(blankFormValues);
  const [activeAttendee, setActiveAttendee] = useState(0);
  const [alert, setAlert] = useState({
    type: "",
    title: "",
    message: "",
  });

  const baseUrl = window.location.origin + window.location.pathname.replace(/\/$/, "");

  // Loads the initial form fields
  useEffect(() => {
    fetchFormFields().then(() => {
      fetchRegistration().then(() => {
        console.log("[event-registration] form fields and registration loaded");
        console.log("[event-registration] app ready");
        setIsLoading(false);
      });
    });
  }, []);

  /**
   * Fetches the form fields for the registration form.
   * Sets the form fields and initial values based on the fetched data.
   */
  const fetchFormFields = async () => {
    try {
      const formFieldsResponse = await fetch(`${baseUrl}/getRegistrationFormFields`);
      if (!formFieldsResponse.ok) {
        throw new Error("Network response was not ok");
      }
      const fieldData = await formFieldsResponse.json();
      setFormFields(fieldData);

      // Dynamically create initial values based on the fetched form fields
      let fetchedInitialValues = fieldData.registrationFields.reduce((acc, field) => {
        acc[field.id] = field.value || field.defaultValue || ""; // Prioritize defaultValue, fall back to value, and default to empty string
        return acc;
      }, {});

      setInitialValues(fetchedInitialValues);
    } catch (error) {
      console.error("[event-registration] error fetching the form fields:", error);
    } finally {
      console.log("[event-registration] form fields loaded");
    }
  };

  /**
   * Fetches the registration submission data from the server.
   * If the registration has already been submitted, it sets the appropriate flags and alerts.
   */
  const fetchRegistration = async () => {
    const registrationSubmissionResponse = await fetch(`${baseUrl}/getRegistrationSubmission`);

    if (!registrationSubmissionResponse.ok) {
      console.error("[event-registration] error fetching a stored registration");
      throw new Error("Failed to fetch registration data");
    }

    const responseJson = await registrationSubmissionResponse.json();

    if (!responseJson.registration) {
      console.log("[event-registration] no stored registration found");
      return;
    }

    console.log("[event-registration] stored registration found");
    const { registration } = responseJson;

    // Load the registration.payload into formik's corresponding values
    let updatedValues = {
      ...form.values, // Merge current form values
      ...registration.Payload, // Merge with fetched registration data
      submission_id: registration.ID, // Update the submission ID
      payment_amount: registration.PaymentAmount,
    };

    // Update Formik values directly
    form.setValues(updatedValues);

    // Check if the registration has already been submitted
    if (registration.Status === "submitted") {
      setAlreadySubmitted(true);

      // Check if payment is pending
      if (registration.PaymentMethod === "creditcard") {
        if (registration.PaymentSuccess == 0) {
          setPaymentPending(true);
          setAlert({ type: "info", title: "Payment is pending for your registration.", message: "Please scroll down to find the credit card payment form." });
        } else {
          setAlert({
            type: "success",
            title: "Thanks, we've received your payment.",
            message: "Your registration has already been submitted and cannot be edited. Please contact us if you have any questions.",
          });
        }
      } else {
        setAlert({
          type: "success",
          title: "Thanks, your registration has been received.",
          message:
            "Please keep an eye on your email for payment information. Your registration has now been submitted and cannot be edited - please contact us if you have any questions.",
        });
      }
    }
  };

  // Set up the validation

  const validate = (values) => {
    setAlert({ type: "", title: "" });
    const errors = {};

    // Function to validate a field based on its configuration and current value
    const validateField = (fieldConfig, fieldValue, basePath) => {
      if (fieldConfig.required) {
        if (fieldConfig.type === "Checkboxes" || fieldConfig.type === "PurchasableItems") {
          // Check if at least one checkbox is checked
          if (!fieldValue || fieldValue.length === 0) {
            console.log("validation error", fieldConfig.name);
            _.set(errors, basePath, "At least one option must be selected.");
          }
        } else {
          // For text, dropdowns, and single checkboxes
          if (!fieldValue || fieldValue === "" || fieldValue === 0) {
            console.log("validation error", fieldConfig.name);
            _.set(errors, basePath, "This field is required.");
          }
          if (fieldConfig.name === "gEmail") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(fieldValue)) {
              console.log("validation error", fieldConfig.name);
              _.set(errors, basePath, "Please enter a valid email address.");
            }
          }
        }
      }
    };

    // Validate registration fields
    formFields.registrationFields.forEach((field) => {
      const fieldValue = values[field.id];
      validateField(field, fieldValue, field.id);
    });

    // Validate attendee fields for each attendee
    if (values.attendees) {
      values.attendees.forEach((attendee, index) => {
        formFields.attendeeFields.forEach((field) => {
          const fieldValue = _.get(attendee, field.id);
          const basePath = `attendees[${index}].${field.id}`;
          validateField(field, fieldValue, basePath);
        });
      });
    }

    // Validate payment fields
    formFields.paymentFields.forEach((field) => {
      const fieldValue = values[field.id];
      validateField(field, fieldValue, field.id);
    });

    return errors;
  };

  // Set up the form

  const form = useFormik({
    initialValues: initialValues,
    initialTouched: {},
    validate,
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      setIsSubmitting(true);

      const updatedValues = {
        ...values,
        submit_type: "submit",
      };

      saveOrSubmitSubmission(updatedValues)
        .then(({ status, code, registrationID, registrationAmount }) => {
          form.setFieldValue("payment_amount", registrationAmount);
          setIsSubmitting(false);

          if (code == "saved-draft") {
            setAlert({ type: "success", title: "Draft saved successfully." });
          } else if (code == "submitted") {
            // check the payment method
            setAlreadySubmitted(true);
            if (form.values.gPaymentMethod == "creditcard") {
              setPaymentPending(true);
              setAlert({ type: "info", title: "Payment is pending for your registration.", message: "Please scroll down to find the credit card payment form." });
            } else {
              setAlert({
                type: "success",
                title: "Your registration has been submitted successfully.",
                message: "Thank you for registering. Please keep an eye on your email for payment information.",
              });
            }
          } else if (code == "submission-failed") {
            setAlert({ type: "error", title: "Submission failed.", message: "Please click 'save draft' to store your progress and try again later or contact us." });
          } else {
            setAlert({ type: "error", title: "An unknown error occurred.", message: "Please click 'save draft' to store your progress and try again later or contact us." });
          }

          // if registrationID is defined and not 0
          if (registrationID) {
            form.setFieldValue("submission_id", registrationID);
          }
          const alertElement = document.querySelector("#event-registration-form-app");
          alertElement.scrollIntoView({ behavior: "smooth" });
        })
        .catch((error) => {
          setIsSubmitting(false);
          setPaymentPending(false);
          setAlert({ type: "error", title: "Unknown error.", message: error.message || "Sorry, an unknown error occurred." });
        });
    },
  });

  // Set up the handlers
  const handleAddAttendee = () => {
    // Initialize a new attendee directly within the handler
    const newAttendee = formFields.attendeeFields.reduce((acc, field) => {
      if (field.type === "Checkboxes" || field.type === "PurchasableItems") {
        acc[field.id] = []; // Initialize arrays for multiple selections
      } else if (field.type === "Checkbox") {
        acc[field.id] = false; // Initialize checkboxes as false
      } else {
        acc[field.id] = field.defaultValue || ""; // Use defaultValue or empty string for other fields
      }
      return acc;
    }, {});

    // Determine if there are any attendees, if not, initialize with the new attendee
    if (!form.values.attendees) {
      form.setFieldValue("attendees", [newAttendee]);
    } else {
      // If attendees already exist, add the new attendee to the list
      const newAttendees = [...form.values.attendees, newAttendee];
      form.setFieldValue("attendees", newAttendees);
      // Focus the new attendee
      setActiveAttendee(newAttendees.length - 1);
    }
  };

  const handleToggleAttendee = (index) => {
    setActiveAttendee(activeAttendee === index ? null : index);
  };

  const handleRemoveAttendee = (index) => {
    const filteredAttendees = form.values.attendees.filter((_, i) => i !== index);
    form.setFieldValue("attendees", filteredAttendees);
  };

  // Save draft bypasses formik's handlesubmit so that we don;t have to deal with errors right away
  const handleSaveDraft = () => {
    setAlert({ type: "", title: "" });
    setIsSubmitting(true);

    const updatedValues = {
      ...form.values,
      submit_type: "draft",
    };

    saveOrSubmitSubmission(updatedValues).then((response) => {
      const { status, code, registrationID, registrationAmount } = response;

      form.setFieldValue("payment_amount", registrationAmount);

      setIsSubmitting(false);

      setAlert({ type: "success", title: "Draft saved successfully." });

      if (registrationID) {
        form.setFieldValue("submission_id", registrationID);
      }
      const alertElement = document.querySelector("#event-registration-form-app");
      alertElement.scrollIntoView({ behavior: "smooth" });
    });
  };

  const handleRegister = () => {
    form.setFieldValue("submit_type", "submit");
    form.handleSubmit();
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this draft registration?")) {
      deleteSubmission().then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
    }
  };

  const saveOrSubmitSubmission = async (values) => {
    try {
      const response = await fetch(`${baseUrl}/saveSubmitRegistrationFormSubmission`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      // TODO: Check what needs to be set to show the form. Loading state?
      if (!response.ok) {
        throw new Error("There was a problem with the network. Please try again later.");
      }
      const data = await response.json();
      console.log(data);
      return data;
    } catch (error) {
      console.error("There was an error saving the form", error);
      return { type: "error", code: error.message || "An unknown error occurred" };
    }
  };

  const deleteSubmission = async () => {
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const response = await fetch(`${baseUrl}/deleteDraftRegistrationFormSubmission`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ submission_id: form.values.submission_id }),
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setAlert({ type: "success", message: data.message });
      form.setValues(initialValues);
    } catch (error) {
      console.error("There was an error deleting the draft submission", error);
      setAlert({ type: "error", message: error.message || "An unknown error occurred" });
    }
  };

  // This represents any cost fields that are directly on the event.
  const calculateRegistrationSubtotal = (registrationFields, formValues) => {
    let total = 0;
    let optionsList = [];
    registrationFields.forEach((field) => {
      if (field.type === "PurchasableItems") {
        const selectionPath = `${field.id}`;
        const selections = _.get(formValues, selectionPath, []);
        selections.forEach((selectedOptionId) => {
          const option = field.purchasableOptions.find((option) => option.id === selectedOptionId);
          if (option) {
            total += option.cost;
            optionsList.push(option.name);
          }
        });
      }
    });
    const options = optionsList.join(", ");
    return { cost: total, options: options };
  };

  // Function to calculate the total cost for an attendee
  const calculateTotalForAttendee = (attendeeIndex, attendeeFields, formValues) => {
    let total = 0;
    let optionsList = [];
    attendeeFields.forEach((field) => {
      if (field.type === "PurchasableItems") {
        const selectionPath = `attendees[${attendeeIndex}].${field.id}`;
        const selections = _.get(formValues, selectionPath, []);
        selections.forEach((selectedOptionId) => {
          const option = field.purchasableOptions.find((option) => option.id === selectedOptionId);
          if (option) {
            total += option.cost;
            optionsList.push(option.name);
          }
        });
      }
    });
    const options = optionsList.join(", ");
    return { cost: total, options: options };
  };

  const attendeeCosts =
    formFields.attendeeFields && form.values.attendees ? form.values.attendees.map((_, idx) => calculateTotalForAttendee(idx, formFields.attendeeFields, form.values)) : [];

  const calculateTotalRegistrationCost = () => {
    const registrationSubtotal = calculateRegistrationSubtotal(formFields.registrationFields, form.values).cost;

    const attendeeTotal = form.values.attendees.reduce((acc, _, index) => {
      return acc + calculateTotalForAttendee(index, formFields.attendeeFields, form.values).cost;
    }, 0);

    return registrationSubtotal + attendeeTotal;
  };

  const getLoadingIndicatorMessage = () => {
    if (form.values.submit_type === "submit") {
      if (form.values.paymentMethod === "credit card") {
        return "Preparing credit card payment form...";
      } else {
        return "Submitting...";
      }
    } else {
      return "Saving draft...";
    }
  };

  // Render the app
  return (
    <div className="c-event-registration-form__wrapper">
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="c-event-registration-form__inner">
          {isSubmitting ? (
            <LoadingIndicator message={form.values.submit_type == "submit" ? "Submitting..." : "Saving draft..."} />
          ) : (
            <div className="c-event-registration-form">
              <form>
                {(alert.title || alert.message) && <AlertBar type={alert.type} title={alert.title} message={alert.message} />}
                {formFields.registrationFields.map((field, index) => (
                  <FormField key={index + field.id} form={form} field={field} readOnly={alreadySubmitted} />
                ))}
                <h3 className="c-event-registration-form__heading">Attendees</h3>
                {form.values.attendees &&
                  form.values.attendees.map((attendee, index) => (
                    <AttendeeFields
                      key={"attendee-" + index}
                      form={form}
                      attendeeFields={formFields.attendeeFields} // Passing attendee fields here
                      attendee={attendee}
                      index={index}
                      onUpdateValue={form.setFieldValue}
                      onRemoveAttendee={() => handleRemoveAttendee(index)}
                      handleToggleAttendee={() => handleToggleAttendee(index)}
                      attendeeActive={activeAttendee === index}
                      alreadySubmitted={alreadySubmitted}
                    />
                  ))}
                {!alreadySubmitted && (
                  <div className="c-event-registration-form__actions">
                    <button type="button" onClick={() => handleAddAttendee()} className="c-event-registration-form__button c-event-registration-form__button-add">
                      Add Attendee
                    </button>
                  </div>
                )}
                <h3 className="c-event-registration-form__heading">Payment Information</h3>
                {formFields.paymentFields.map((field, index) => (
                  <FormField key={index + field.id} form={form} field={field} readOnly={alreadySubmitted} />
                ))}
                {/* Attendees Total Table */}
                {form.values.attendees && form.values.attendees.length > 0 && (
                  <table className="c-event-registration-form__subtotal-table">
                    <thead>
                      <tr className="c-event-registration-form__table-header-row">
                        <th>Attendee</th>
                        <th className="c-event-registration-form__table-cell-cost">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.values.attendees &&
                        form.values.attendees.map((attendee, index) => (
                          <tr key={index}>
                            <td>
                              {index + 1}
                              {form.values.attendees &&
                                form.values.attendees[index] &&
                                (form.values.attendees[index].gFirstName || form.values.attendees[index].gLastName) &&
                                ` - ${form.values.attendees[index].gFirstName}  ${form.values.attendees[index].gLastName} `}
                              - {attendeeCosts[index].options}
                            </td>
                            <td className="c-event-registration-form__table-cell-cost">${attendeeCosts[index].cost}</td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot>
                      {calculateRegistrationSubtotal(formFields.registrationFields, form.values).cost > 0 && (
                        <tr>
                          <td>
                            <strong>Registration - {calculateRegistrationSubtotal(formFields.registrationFields, form.values).options}</strong>
                          </td>
                          <td className="c-event-registration-form__table-cell-cost">${calculateRegistrationSubtotal(formFields.registrationFields, form.values).cost}</td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <strong>Total</strong>
                        </td>
                        <td className="c-event-registration-form__table-cell-cost">${calculateTotalRegistrationCost()}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}

                {(alert.title || alert.message) && <AlertBar type={alert.type} title={alert.title} message={alert.message} />}

                {!alreadySubmitted && (
                  <div className="c-event-registration-form__actions">
                    <div className="c-event-registration-form__actions-right">
                      {!alreadySubmitted && (
                        <div type="button" onClick={() => handleDelete()} className="c-event-registration-form__button c-event-registration-form__button-delete">
                          Delete
                        </div>
                      )}
                    </div>
                    <div className="c-event-registration-form__actions-left">
                      {!alreadySubmitted && (
                        <div type="button" onClick={() => handleSaveDraft()} className="c-event-registration-form__button c-event-registration-form__button-save">
                          Save draft
                        </div>
                      )}

                      <div type="button" onClick={() => handleRegister()} className="c-event-registration-form__button c-event-registration-form__button-submit">
                        {form.values.gPaymentMethod == "creditcard" ? "Proceed to payment" : "Register"}
                      </div>
                    </div>
                  </div>
                )}
                {form.values.submit_type == "submit" && Object.keys(form.errors).length > 0 && (
                  <div className="c-event-registration-form__general-error-wrapper">
                    <div className="c-event-registration-form__general-error-title">Sorry, there's a problem with your registration.</div>
                    <div className="c-event-registration-form__general-error-message">
                      Please check form for missing required information and other errors. Look for the red notices.
                    </div>
                  </div>
                )}
              </form>
              {alreadySubmitted && paymentPending && <StripeProvider registrationID={form.values.submission_id} paymentAmount={form.values.payment_amount} />}

              <div className="c-event-registration-form__debug">
                <div className="c-event-registration-form__accordion">
                  <div className="c-event-registration-form__accordion-item">
                    <input type="checkbox" id="accordion-1" className="c-event-registration-form__accordion-toggle" />
                    <label htmlFor="accordion-1" className="c-event-registration-form__accordion-title">
                      initialValues
                    </label>
                    <div className="c-event-registration-form__accordion-content">
                      <pre className="c-event-registration-form__debug-wrapper">{JSON.stringify(form.initialValues, null, 2)}</pre>
                    </div>
                  </div>

                  <div className="c-event-registration-form__accordion-item">
                    <input type="checkbox" id="accordion-2" className="c-event-registration-form__accordion-toggle" />
                    <label htmlFor="accordion-2" className="c-event-registration-form__accordion-title">
                      values
                    </label>
                    <div className="c-event-registration-form__accordion-content">
                      <pre className="c-event-registration-form__debug-wrapper">{JSON.stringify(form.values, null, 2)}</pre>
                    </div>
                  </div>

                  <div className="c-event-registration-form__accordion-item">
                    <input type="checkbox" id="accordion-3" className="c-event-registration-form__accordion-toggle" />
                    <label htmlFor="accordion-3" className="c-event-registration-form__accordion-title">
                      formFields
                    </label>
                    <div className="c-event-registration-form__accordion-content">
                      <pre className="c-event-registration-form__debug-wrapper">{JSON.stringify(formFields, null, 2)}</pre>
                    </div>
                  </div>

                  <div className="c-event-registration-form__accordion-item">
                    <input type="checkbox" id="accordion-4" className="c-event-registration-form__accordion-toggle" />
                    <label htmlFor="accordion-4" className="c-event-registration-form__accordion-title">
                      errors
                    </label>
                    <div className="c-event-registration-form__accordion-content">
                      <pre className="c-event-registration-form__debug-wrapper">{JSON.stringify(form.errors, null, 2)}</pre>
                    </div>
                  </div>

                  <div className="c-event-registration-form__accordion-item">
                    <input type="checkbox" id="accordion-5" className="c-event-registration-form__accordion-toggle" />
                    <label htmlFor="accordion-5" className="c-event-registration-form__accordion-title">
                      touched
                    </label>
                    <div className="c-event-registration-form__accordion-content">
                      <pre className="c-event-registration-form__debug-wrapper">{JSON.stringify(form.touched, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default App;
